import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import {
  IResponseHandlerOptions,
  useResponseHandler,
} from 'utils/service/responseHandler';
import {
  ICreateEventCost,
  IEventCostResponse,
  IUpdateEventCost,
  IDeleteEventCost,
} from './types';

export const useGetEventCosts = (eventId: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IEventCostResponse[], DefaultError>({
    queryKey: ['event-costs', eventId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `EventCost/Event`,
        params: { eventId },
      });

      return responseHandler(response, {
        groupName: `useGetEventCosts - ${eventId}`,
        notFoundPath: '/events',
      });
    },
    enabled: enabled,
  });
};

export const useGetEventCost = (
  id: string,
  options?: IResponseHandlerOptions<IEventCostResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IEventCostResponse, DefaultError>({
    queryKey: ['event-cost', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `EventCost/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetEventCost - ${id}`,
        notFoundPath: '/events',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const useCreateEventCost = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('createEventCost');

  return useMutation<IEventCostResponse, DefaultError, ICreateEventCost>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: body.costName,
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `EventCost`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `createEventCost`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.costName,
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateEventCost = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('updateEventCost');

  return useMutation<unknown, DefaultError, IUpdateEventCost>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.update.loading', {
          what: body.costName,
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `EventCost`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `updateEventCost`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('event-cost.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteEventCost = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('deleteEventCost');

  return useMutation<unknown, DefaultError, IDeleteEventCost>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.remove.loading', {
          what: i18next.t('event-cost.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `EventCost`,
        body,
      });

      return responseHandler(response, {
        groupName: `deleteEventCost`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('event-cost.what'),
        })
      );
    },
    onError: dismiss,
  });
};
